import { ComponentInterface, Entity, lerp } from '@goodboydigital/odie';
import { Container } from 'pixi.js';

import { Shared } from '../../App';
import { DebugAdjuster } from '../../ui/DebugAdjuster';
import { CharacterData } from '../entities/CharacterEntity';
import { VehicleEntity } from '../entities/VehicleEntity';

export class SpineDebugComponent implements ComponentInterface
{
    public vehicle!: VehicleEntity;
    public isOn = false;
    public view: Container;
    public scaleAdjuster: DebugAdjuster;
    public offsetXAdjuster: DebugAdjuster;
    public offsetYAdjuster: DebugAdjuster;

    private _data!: CharacterData;

    constructor()
    {
        this.view = new Container();
        this.scaleAdjuster = new DebugAdjuster({
            increment: 0.01,
            min: 0.01,
            max: 2,
            roundToPoints: 2,
        });
        this.offsetXAdjuster = new DebugAdjuster({
            increment: 1,
            min: -20,
            max: 20,
        });
        this.offsetYAdjuster = new DebugAdjuster({
            increment: 1,
            min: 0,
            max: 200,
        });
        this.view.addChild(this.scaleAdjuster);
        this.view.addChild(this.offsetXAdjuster);
        this.view.addChild(this.offsetYAdjuster);

        this.offsetXAdjuster.y = -60;
        this.offsetYAdjuster.y = -30;

        this.scaleAdjuster.onUpdated.connect(this._updateSize.bind(this));
        this.offsetXAdjuster.onUpdated.connect(this._updateOffsetX.bind(this));
        this.offsetYAdjuster.onUpdated.connect(this._updateOffsetY.bind(this));
    }

    public addedToEntity(entity: Entity): void
    {
        if (!(entity instanceof VehicleEntity)) throw new Error('Tag component only works with a VehicleEntity');
        this.vehicle = entity as VehicleEntity;
        this.vehicle.view.addChild(this.view);

        this.vehicle.physics.physicsFreeUpdateRunner.add(this as any);
        this.vehicle.onRespawn.connect(() => this._turnOn());
        this.isOn = false;
        this.view.renderable = false;
    }

    private _turnOn(): void
    {
        if (this.isOn) return;
        this.isOn = true;
        this.view.x = this.vehicle.character.physics.x;
        this.view.y = this.vehicle.character.physics.y;

        this._data = Shared.assets.getData<CharacterData>(`character-${this.vehicle.data.character}`);

        this.scaleAdjuster.value = this._data.spineScale;
        this.offsetXAdjuster.value = this._data.spineX;
        this.offsetYAdjuster.value = this._data.spineY;

        this.view.renderable = true;
        this.physicsFreeUpdate();
    }

    private _updateSize(): void
    {
        if (!this.vehicle.character.spine.spine) return;
        this._data.spineScale = this.scaleAdjuster.value;
        this.vehicle.character.spine.spine?.scale.set(this.scaleAdjuster.value);
    }

    private _updateOffsetX(): void
    {
        if (!this.vehicle.character.spine.spine) return;
        this._data.spineX = this.offsetXAdjuster.value;
        this.vehicle.character.spine.spine.x = this.offsetXAdjuster.value;
    }

    private _updateOffsetY(): void
    {
        if (!this.vehicle.character.spine.spine) return;
        this._data.spineY = this.offsetYAdjuster.value;
        this.vehicle.character.spine.spine.y = this.offsetYAdjuster.value;
    }

    public physicsFreeUpdate(): void
    {
        if (!this.vehicle) return;
        if (this.isOn)
        {
            const a = lerp(this.view.rotation, -this.vehicle.character.physics.angle, 0.05);
            const r = a + Math.PI;
            const x = this.vehicle.character.physics.x + (Math.sin(-r) * 70);

            this.view.x = lerp(this.view.x, x, 0.6);
            this.view.y = -200;
            this.view.renderable = this.vehicle.chassis.view.renderable;
        }
        else
        {
            this.view.x = this.vehicle.character.physics.x;
            this.view.y = this.vehicle.character.physics.y;
            this.view.rotation = 0;
            this.view.renderable = false;
        }
    }

    get data(): CharacterData
    {
        return this._data;
    }
}
